import { Button, Stack, Text, Theme } from '@ltvco/refresh-lib/theme';
import {
  AttachMoneyOutlined,
  CarRepair,
  LocalOfferOutlined,
  PlaylistAddCheck,
  ChevronRight,
} from '@mui/icons-material';
import {
  OrangeCheckCircle,
  GreyCancelOutlined,
  ActivateFreeAccessContainer,
  PlanDetailsTable,
  GridRow,
  PlanDetailItemIconContainer,
  PlanDetailItemText,
  BumperColumnHeader,
  PlanDetailItemContainer,
  TitleTag,
  HeaderText,
  SubHeaderText,
  GarageDetailsHeader,
  GarageDetailsTable,
  GarageDetailItemContainer,
  HeaderTextAccentColor,
  ActivateButton,
} from './components';
import { useNavigate } from 'react-router-dom';
import { useReactivateWithPlanChange } from 'hooks/useReactivateWithPlanChange';

const BUMPER_FREE_ACCESS_PLAN_ID = 3403;

export function ActivateFreeAccess() {
  const navigate = useNavigate();
  const { reactivateWithPlanChangeMutation } = useReactivateWithPlanChange();

  const handleReactivationClick = () => {
    reactivateWithPlanChangeMutation.mutate({
      planId: BUMPER_FREE_ACCESS_PLAN_ID,
    });
  };

  const handleProClick = () => {
    navigate('/reactivation');
  };

  const gridData = [
    [
      'View vehicle specs by VIN, license plate, or model search',
      <OrangeCheckCircle fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
    [
      'Track registration, oil changes, and more with Bumper Garage',
      <OrangeCheckCircle fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
    [
      'Get exclusive perks and discounts via Bumper Rewards',
      <OrangeCheckCircle fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
    [
      'Browse inventory from 50,000+ dealerships in Bumper Marketplace',
      <OrangeCheckCircle fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
    [
      'Access full Bumper history reports with NMVTIS data, accidents* history, purchase history and more',
      <GreyCancelOutlined fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
    [
      'Monitor vehicles and get timely report updates',
      <GreyCancelOutlined fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
    [
      'Use AI-powered Smart Insights to value, buy, or sell based on unique vehicle history',
      <GreyCancelOutlined fontSize="large" />,
      <OrangeCheckCircle fontSize="large" />,
    ],
  ];

  return (
    <ActivateFreeAccessContainer>
      <Stack>
        <TitleTag>Bumper Garage</TitleTag>
        <HeaderText variant="h1">
          Keep your car organized, maintained, and running strong for{' '}
          <HeaderTextAccentColor color="#ff4722">free!</HeaderTextAccentColor>
        </HeaderText>
        <SubHeaderText>
          Your subscription may have ended, but we’re keeping your Bumper Garage
          open, free of charge.{' '}
        </SubHeaderText>
        <GarageDetailsHeader variant="h2">
          With your Garage, you can:
        </GarageDetailsHeader>
        <GarageDetailsTable>
          <li>
            <GarageDetailItemContainer color="text.secondary" direction="row">
              <CarRepair />
              <Text fontWeight={700}>
                Track maintenance, repairs, and service records
              </Text>
            </GarageDetailItemContainer>
          </li>
          <li>
            <GarageDetailItemContainer color="text.secondary" direction="row">
              <LocalOfferOutlined />
              <Text fontWeight={700}>Shop smarter on Bumper Marketplace</Text>
            </GarageDetailItemContainer>
          </li>
          <li>
            <GarageDetailItemContainer color="text.secondary" direction="row">
              <AttachMoneyOutlined />
              <Text fontWeight={700}>
                Get exclusive perks and discounts via Bumper Rewards
              </Text>
            </GarageDetailItemContainer>
          </li>
          <li>
            <GarageDetailItemContainer color="text.secondary" direction="row">
              <PlaylistAddCheck />
              <Text fontWeight={700}>
                Stay on top of your car’s care routine
              </Text>
            </GarageDetailItemContainer>
          </li>
        </GarageDetailsTable>
        <ActivateButton onClick={handleReactivationClick}>
          Keep Bumper Garage Free
          <ChevronRight />
        </ActivateButton>

        <PlanDetailsTable container direction={'column'}>
          <GridRow container height={8}>
            <PlanDetailItemIconContainer size={{ xs: 6, md: 8 }} />
            <PlanDetailItemIconContainer
              className="bumper-garage-header bumper-garage-header--extra"
              size={{ xs: 3, md: 2 }}
            />
            <PlanDetailItemIconContainer size={{ xs: 3, md: 2 }} />
          </GridRow>
          <GridRow container>
            <PlanDetailItemIconContainer
              size={{ xs: 6, md: 8 }}
              display={'flex'}
              className="justify-start"
            >
              <Stack>
                <BumperColumnHeader>Under the Hood</BumperColumnHeader>
              </Stack>
            </PlanDetailItemIconContainer>
            <PlanDetailItemIconContainer
              className="bumper-garage-header"
              size={{ xs: 3, md: 2 }}
              container
            >
              <BumperColumnHeader className="white-text" textAlign={'center'}>
                Bumper
                <br />
                Garage
              </BumperColumnHeader>
            </PlanDetailItemIconContainer>
            <PlanDetailItemIconContainer
              className="bumper-pro-header"
              size={{ xs: 3, md: 2 }}
              container
            >
              <BumperColumnHeader className="white-text" textAlign={'center'}>
                Bumper Pro
              </BumperColumnHeader>
            </PlanDetailItemIconContainer>
          </GridRow>
          {gridData.map((gridRowData, index) => {
            return (
              <GridRow container key={`grid-row-${index}`}>
                <PlanDetailItemContainer size={{ xs: 6, md: 8 }} container>
                  <PlanDetailItemText>{gridRowData[0]}</PlanDetailItemText>
                </PlanDetailItemContainer>
                <PlanDetailItemIconContainer
                  className="bumper-garage-item"
                  size={{ xs: 3, md: 2 }}
                  container
                >
                  {gridRowData[1]}
                </PlanDetailItemIconContainer>
                <PlanDetailItemIconContainer size={{ xs: 3, md: 2 }} container>
                  {gridRowData[2]}
                </PlanDetailItemIconContainer>
              </GridRow>
            );
          })}
          <GridRow container>
            <PlanDetailItemIconContainer size={{ xs: 6, md: 8 }} />
            <PlanDetailItemIconContainer
              className="bumper-garage-item"
              size={{ xs: 3, md: 2 }}
            />
            <PlanDetailItemIconContainer size={{ xs: 3, md: 2 }} container>
              <ActivateButton onClick={handleProClick}>PRO</ActivateButton>
            </PlanDetailItemIconContainer>
          </GridRow>
        </PlanDetailsTable>
        <ActivateButton onClick={handleReactivationClick}>
          Keep Bumper Garage Free
          <ChevronRight />
        </ActivateButton>
      </Stack>
    </ActivateFreeAccessContainer>
  );
}
